import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, useTelefon } from "@kundeninfo/ui";
import { Register, Reiter } from "@kundeninfo/ui/me-design";
import { TicketBoard, fokussiereKarte } from "./TicketBoard.js";
import { TicketListe } from "./TicketListe.js";
import { TicketDialog } from "./TicketDialog.js";
import type { TicketDialogProps } from "./TicketDialog.js";
import { TicketAnlegen } from "./TicketAnlegen.js";
import { TicketVerknuepfungen } from "./TicketVerknuepfungen.js";
import { leseFilter } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import { berlinerTag, heuteBeschriftung, tagBeschriftung } from "./ticketFormat.js";
import type { TicketsClient } from "./ticketsClient.js";
import type { VerlassenWaechter } from "../../router.js";
import "./Tickets.css";

/**
 * DER BEREICH „TICKETS" (Spec „Board › Aufbau", F43, F129): oben die Reiterzeile Board | Liste
 * (`me-register`, Adresse F19) mit dem Zähler „offen" am Reiter Board (vom Board gemeldet, mit Filtern,
 * 61i) und dem Datum rechts (61o, am Telefon kurz); darunter das Panel des aktiven Reiters mit seiner
 * Werkzeugleiste. Das Ticketpanel über `/tickets/<id>` (Direktaufruf, schließt zurück auf die Ansicht
 * darunter; Paket 1c F121: am Desktop rechts über dem sichtbaren, abgedunkelten Board) und
 * „+ Aufgabe" (in der Werkzeugleiste von Board bzw. Liste). Nach jeder Sofort-Schreibhandlung im Panel,
 * beim Schließen nach Änderungen und nach dem Anlegen lädt die Ansicht darunter neu (`neuLaden`,
 * Rollposition bleibt, F20). Beim Schließen geht der Fokus an die Karte des Tickets, wenn me-dialog ihn
 * nicht schon an den Auslöser zurückgeben konnte (die Karte kann nach dem Neuladen eine andere sein);
 * fehlt sie, an den aktiven Reiter (F129).
 *
 * Die vier Filter teilen Board und Liste (Ruling A4) über EIN Gedächtnis (`boardSpeicher.ts`):
 * Das Board führt und merkt sie selbst; die Liste bekommt sie von hier (`filter`/`onFilter`).
 * Der Bereich hält den Filter der Liste im Speicher der Sitzung und übernimmt beim Wechsel vom
 * Board nur, was das Board im Gedächtnis GEÄNDERT hat — so überlebt die Wahl der Liste auch
 * ohne Browserspeicher (privates Fenster), und eine Wahl im Board kommt in der Liste an.
 *
 * Der Dialog trägt die Ticket-Kennung als `key`: Wechselt die Adresse auf ein anderes Ticket,
 * beginnen Entwürfe und Startversion frisch (Review 15). Weil er an der Adresse hängt, fragt
 * ein Verlassen-Wächter (Hausmuster, `registriereVerlassenWaechter`) gestapelt nach, solange
 * im Dialog oder in „Aufgabe anlegen“ Ungespeichertes steht — Zurück-Taste, Bereichswechsel
 * und Ansichtswechsel verwerfen sonst still (Review 17). Schließt der Dialog selbst (er hat
 * dann schon gefragt), gibt der Bereich den Weg frei, damit nicht zweimal gefragt wird.
 */
export type TicketsRoute = { ansicht: "board" | "liste"; id: string | null };

/** Kennungen der Reiter und ihrer Panels (F129: `fuer` am Reiter, `id` am Panel). */
const PANEL = { board: "tickets-board", liste: "tickets-liste" } as const;

export type BereichTicketsProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string; istAdmin: boolean };
  arbeitsunternehmenId: string | null; route: TicketsRoute; onNavigiere: (r: TicketsRoute) => void;
  onZurAkte: (customerId: string) => void; zusatzReiter?: TicketDialogProps["zusatzReiter"];
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

function gleicherFilter(a: BoardFilter, b: BoardFilter): boolean {
  return a.company === b.company && a.customer === b.customer && a.assignee === b.assignee && a.linked === b.linked
    && (a.team ?? "") === (b.team ?? "");
}

export function BereichTickets(p: BereichTicketsProps): ReactElement {
  const [neuLaden, setNeuLaden] = useState(0);
  const neuLadenAnstossen = useCallback(() => setNeuLaden((n) => n + 1), []);
  const telefon = useTelefon();
  // F121: Schließen gibt den Fokus an die Karte zurück, sofern sie noch da ist. me-dialog gibt ihn
  // selbst an den Auslöser zurück; hat das Board die Karte nach einer Änderung neu gezeichnet
  // (andere Spalte) oder wurde das Ticket direkt aufgerufen, stünde er sonst auf <body>. Wandert
  // die Karte erst mit dem Neuladen nach dem Schließen, holt `TicketBoard` den Fokus nach. Fehlt
  // die Karte (Filter, anderes Unternehmen, Liste), geht er an den aktiven Reiter, ohne zu rollen;
  // nimmt der keinen Fokus an, an die Sektion der Ansicht (F129).
  const offenesTicket = useRef<string | null>(null);
  useEffect(() => {
    const vorher = offenesTicket.current;
    offenesTicket.current = p.route.id;
    if (vorher === null || p.route.id !== null) return;
    const aktiv = document.activeElement;
    if (aktiv !== null && aktiv !== document.body) return;
    if (fokussiereKarte(document, vorher)) return;
    document.querySelector<HTMLElement>(`me-reiter[fuer="${PANEL[p.route.ansicht]}"]`)?.focus({ preventScroll: true });
    const jetzt = document.activeElement;
    if (jetzt !== null && jetzt !== document.body) return;
    document.querySelector<HTMLElement>(p.route.ansicht === "liste" ? "section.tl" : "section.tb")
      ?.focus({ preventScroll: true });
  }, [p.route.id, p.route.ansicht]);
  // F129 (61c, 61i): Zähler „offen“ (mit Filtern) und der Tag des Servers kommen vom Board. Die Liste
  // lädt das Board nicht — dort gibt es keinen Zähler, und das Datum ist der Berliner Tag (F13).
  const [kopf, setKopf] = useState<{ offen: number; heute: string } | null>(null);
  const offenGemeldet = useCallback((offen: number, heute: string) => {
    setKopf((alt) => (alt !== null && alt.offen === offen && alt.heute === heute ? alt : { offen, heute }));
  }, []);
  const [anlegen, setAnlegen] = useState<{ companyId: string | null } | null>(null);
  const companyIds = p.companies.map((c) => c.id);
  const [filter, setFilter] = useState<BoardFilter>(() => leseFilter(companyIds));
  // Das Gedächtnis beim Betreten des Boards: Beim Zurück in die Liste zählt nur, was das Board
  // daran geändert hat. Abgeglichen wird noch im selben Zeichnen (Muster „Zustand bei
  // geänderter Eingabe zurücksetzen"), damit die Liste gleich mit dem richtigen Filter lädt.
  const [beimBoard, setBeimBoard] = useState<BoardFilter | null>(() =>
    (p.route.ansicht === "board" ? leseFilter(companyIds) : null));
  const [ansichtVorher, setAnsichtVorher] = useState(p.route.ansicht);
  if (ansichtVorher !== p.route.ansicht) {
    setAnsichtVorher(p.route.ansicht);
    // Eine Zahl aus einem älteren Stand wäre falsch (F129): Der Zähler kommt neu, sobald das Board lädt.
    setKopf(null);
    const jetzt = leseFilter(companyIds);
    if (p.route.ansicht === "board") setBeimBoard(jetzt);
    else if (beimBoard === null || !gleicherFilter(jetzt, beimBoard)) setFilter(jetzt);
  }

  // Ungespeichertes im Dialog (je Kennung) bzw. in „Aufgabe anlegen“ — nur solange das
  // Gemeldete zum Offenen passt (ein ausgehängter Dialog meldet nichts mehr).
  const [dialogOffen, setDialogOffen] = useState<{ id: string; ja: boolean }>({ id: "", ja: false });
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const ungespeichert = (p.route.id !== null && dialogOffen.id === p.route.id && dialogOffen.ja)
    || (anlegen !== null && anlegenOffen);
  const freigabe = useRef(false);
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!ungespeichert || registriere === undefined) return undefined;
    freigabe.current = false;
    return registriere(() => (freigabe.current ? true : darfAbbrechen(true)));
  }, [ungespeichert, registriere]);
  const dialogMeldet = useCallback((ja: boolean) => {
    if (p.route.id !== null) setDialogOffen({ id: p.route.id, ja });
  }, [p.route.id]);
  /** Ein Weg, den der Dialog selbst beschlossen hat (Speichern, Abbrechen nach seiner Frage,
   *  Anlegen): ohne zweite Frage. */
  const freigeben = () => {
    freigabe.current = true;
    setDialogOffen({ id: "", ja: false });
    setAnlegenOffen(false);
  };

  // Im Board der Tag des Servers (Berliner Tag aus `board.heute`), sonst der Berliner Tag der Uhr.
  const heute = p.route.ansicht === "board" && kopf !== null ? kopf.heute : berlinerTag(new Date().toISOString());
  const oeffnen = (id: string) => p.onNavigiere({ ...p.route, id });
  return (
    <div className="bt">
      <Register className="bt__reiter" aria-label="Ansicht" aktiv={PANEL[p.route.ansicht]}
        onReiterGewaehlt={(d) => p.onNavigiere({ ansicht: d.id === PANEL.liste ? "liste" : "board", id: null })}
        rechts={(
          <span className="bt__heute">
            {telefon
              ? <><span className="bt__unsichtbar">Heute </span>{tagBeschriftung(heute)}</>
              : `Heute ${heuteBeschriftung(heute)}`}
          </span>
        )}>
        <Reiter fuer={PANEL.board} beschriftung="Board"
          {...(p.route.ansicht === "board" && kopf !== null ? { zaehler: kopf.offen, zaehlerText: "offen" } : {})} />
        <Reiter fuer={PANEL.liste} beschriftung="Liste" />
      </Register>
      {/* Das Panel des aktiven Reiters (Hausregel „Reiter nach unten offen“): die Ansicht samt Leiste. */}
      <div className="bt__panel" id={PANEL[p.route.ansicht]} role="tabpanel"
        aria-label={p.route.ansicht === "liste" ? "Liste" : "Board"}>
        {p.route.ansicht === "board"
          ? <TicketBoard client={p.client} companies={p.companies} ich={p.ich} onOeffnen={oeffnen}
              onAnlegen={(companyId) => setAnlegen({ companyId })} neuLaden={neuLaden} onOffen={offenGemeldet} />
          : <TicketListe client={p.client} companies={p.companies} onOeffnen={oeffnen} ichId={p.ich.id}
              neuLaden={neuLaden} filter={filter} onFilter={setFilter}
              onAnlegen={() => setAnlegen({ companyId: null })} />}
      </div>
      {anlegen !== null && (
        <TicketAnlegen client={p.client} companies={p.companies} ich={p.ich} vorgabeCompanyId={anlegen.companyId}
          arbeitsunternehmenId={p.arbeitsunternehmenId} onUngespeichert={setAnlegenOffen}
          onAbbrechen={() => { freigeben(); setAnlegen(null); }}
          onAngelegt={(id) => { freigeben(); setAnlegen(null); setNeuLaden((n) => n + 1); oeffnen(id); }} />
      )}
      {p.route.id !== null && (
        <TicketDialog key={p.route.id} client={p.client} ticketId={p.route.id} companies={p.companies}
          ich={{ id: p.ich.id, istAdmin: p.ich.istAdmin }} onZurAkte={p.onZurAkte}
          pfad={p.route.ansicht === "liste" ? "Tickets › Liste" : "Tickets › Board"}
          onUngespeichert={dialogMeldet}
          onGeaendert={neuLadenAnstossen}
          zusatzReiter={[...(p.zusatzReiter ?? []), { id: "verknuepfungen", label: "Verknüpfungen",
            inhalt: (d, neu, nachladen) => <TicketVerknuepfungen d={d} client={p.client} onNeu={neu} nachladen={nachladen} /> }]}
          onSchliessen={(geaendert) => {
            freigeben();
            if (geaendert) setNeuLaden((n) => n + 1);
            p.onNavigiere({ ...p.route, id: null });
          }} />
      )}
    </div>
  );
}
